import { X } from "@phosphor-icons/react";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect } from "react";

interface ConnectedAppsDialogProps {
  showDialog: boolean;
  onClose: () => void;
}

export const ConnectedAppsDialog = ({
  showDialog,
  onClose,
}: ConnectedAppsDialogProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (showDialog) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showDialog, onClose]);

  if (!showDialog) return null;

  return (
    <Dialog.Root open={showDialog} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="bg-black/60 fixed inset-0 z-40 backdrop-blur-sm"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
          onClick={onClose}
        />
        <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Clickable overlay area */}
          <div
            className="absolute inset-0"
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onClose();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close dialog"
            data-testid="connected-apps-dialog-backdrop"
          />
          <div className="relative z-10 w-[600px] max-w-[90vw] overflow-hidden rounded-xl bg-base-white shadow-2xl">
            {/* Header */}
            <div className="bg-white relative p-6">
              <div className="flex flex-col items-start justify-center gap-2 pr-12">
                <h2 className="font-['National_2'] text-[18px] font-medium leading-[26px] tracking-[-0.04px] text-[#21201C]">
                  Connect Your Internal Tools for Deeper Scans
                </h2>
                <p className="font-['National_2'] text-[14px] font-normal leading-[20px] tracking-normal text-[rgba(8,8,0,0.632)]">
                  Connect your design files, brand guidelines, and campaign
                  tools—it&apos;s one click, and I&apos;ll scan more of your
                  marketing compliance issues.
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  backgroundColor: "white",
                  border: "1px solid #374151",
                  color: "#374151",
                }}
                className="hover:bg-gray-50 absolute right-4 top-4 flex size-5 items-center justify-center rounded-full transition-colors"
                data-testid="close-dialog-button"
              >
                <X size={10} />
              </button>
            </div>

            {/* Tools List */}
            <div className="bg-white px-6 pb-6">
              <div className="space-y-0">
                {/* Figma */}
                <div className="border-gray-100 flex items-center justify-between border-b py-4">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white flex size-10 items-center justify-center rounded">
                      <svg
                        width="16"
                        height="24"
                        viewBox="0 0 16 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_17025_51696)">
                          <path
                            d="M4 24C6.208 24 8 22.208 8 20V16H4C1.792 16 0 17.792 0 20C0 22.208 1.792 24 4 24Z"
                            fill="#0ACF83"
                          />
                          <path
                            d="M0 12C0 9.792 1.792 8 4 8H8V16H4C1.792 16 0 14.208 0 12Z"
                            fill="#A259FF"
                          />
                          <path
                            d="M0 4C0 1.792 1.792 0 4 0H8V8H4C1.792 8 0 6.208 0 4Z"
                            fill="#F24E1E"
                          />
                          <path
                            d="M8 0H12C14.208 0 16 1.792 16 4C16 6.208 14.208 8 12 8H8V0Z"
                            fill="#FF7262"
                          />
                          <path
                            d="M16 12C16 14.208 14.208 16 12 16C9.792 16 8 14.208 8 12C8 9.792 9.792 8 12 8C14.208 8 16 9.792 16 12Z"
                            fill="#1ABCFE"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_17025_51696">
                            <rect width="16" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">Figma</h3>
                      <p className="text-gray-600 text-sm">
                        Design files, brand assets, and style guides
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #374151",
                      color: "#374151",
                    }}
                    className="hover:bg-gray-50 rounded-md px-4 py-2 font-medium transition-colors"
                    data-testid="connected-apps-connect-figma"
                  >
                    Connect
                  </button>
                </div>

                {/* Asana */}
                <div className="border-gray-100 flex items-center justify-between border-b py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex size-10 items-center justify-center">
                      <svg
                        width="24"
                        height="22"
                        viewBox="0 0 24 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_17025_51710)">
                          <path
                            d="M18.7805 11.6284C15.8977 11.6284 13.5609 13.9423 13.5609 16.7968C13.5609 19.6512 15.8977 21.9651 18.7805 21.9651C21.6632 21.9651 24 19.6511 24 16.7968C24 13.9423 21.6632 11.6284 18.7805 11.6284ZM5.21953 11.6289C2.33691 11.6289 0 13.9423 0 16.7968C0 19.6512 2.33691 21.9651 5.21953 21.9651C8.10234 21.9651 10.4393 19.6511 10.4393 16.7968C10.4393 13.9423 8.10234 11.6289 5.21953 11.6289ZM17.2195 5.16814C17.2195 8.02257 14.8826 10.3368 12.0001 10.3368C9.11728 10.3368 6.78047 8.02257 6.78047 5.16814C6.78047 2.31399 9.11719 0 12 0C14.8825 0 17.2193 2.31418 17.2193 5.16805"
                            fill="#F06A6A"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_17025_51710">
                            <rect width="24" height="21.9651" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">Asana</h3>
                      <p className="text-gray-600 text-sm">
                        Marketing campaigns and project files
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #374151",
                      color: "#374151",
                    }}
                    className="hover:bg-gray-50 rounded-md px-4 py-2 font-medium transition-colors"
                    data-testid="connected-apps-connect-asana"
                  >
                    Connect
                  </button>
                </div>

                {/* Microsoft 365 */}
                <div className="border-gray-100 flex items-center justify-between border-b py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex size-10 items-center justify-center">
                      <svg
                        width="24"
                        height="23"
                        viewBox="0 0 24 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.4764 2.39166C17.152 1.3666 16.2007 0.669922 15.1256 0.669922H14.3129C13.1253 0.669922 12.1064 1.51651 11.8889 2.68403L10.6813 9.16535L11.0437 7.96893C11.3586 6.92904 12.317 6.21785 13.4036 6.21785H17.178L19.3422 6.9752L20.4933 6.22173C19.4181 6.22173 18.4669 5.5212 18.1424 4.49613L17.4764 2.39166Z"
                          fill="url(#paint0_radial_17090_63392)"
                        />
                        <path
                          d="M6.77753 19.6495C7.09854 20.68 8.05239 21.3819 9.13174 21.3819H10.7315C12.0796 21.3819 13.1778 20.2993 13.197 18.9513L13.2817 12.9971L12.9572 14.0775C12.6441 15.1201 11.6843 15.834 10.5956 15.834H6.82192L4.87039 14.9443L3.77448 15.834C4.85378 15.834 5.80767 16.536 6.12868 17.5664L6.77753 19.6495Z"
                          fill="url(#paint1_radial_17090_63392)"
                        />
                        <path
                          d="M14.9589 0.669922H6.32877C3.86301 0.669922 2.38357 3.84974 1.39727 7.02958C0.228755 10.7969 -1.30027 15.8353 3.12329 15.8353H6.88142C7.97773 15.8353 8.94164 15.1141 9.24986 14.062C9.89829 11.8487 11.0316 7.99763 11.9221 5.065C12.3754 3.5724 12.7529 2.29053 13.3323 1.49225C13.6571 1.04471 14.1985 0.669922 14.9589 0.669922Z"
                          fill="url(#paint2_linear_17090_63392)"
                        />
                        <path
                          d="M14.9589 0.669922H6.32877C3.86301 0.669922 2.38357 3.84974 1.39727 7.02958C0.228755 10.7969 -1.30027 15.8353 3.12329 15.8353H6.88142C7.97773 15.8353 8.94164 15.1141 9.24986 14.062C9.89829 11.8487 11.0316 7.99763 11.9221 5.065C12.3754 3.5724 12.7529 2.29053 13.3323 1.49225C13.6571 1.04471 14.1985 0.669922 14.9589 0.669922Z"
                          fill="url(#paint3_linear_17090_63392)"
                        />
                        <path
                          d="M9.03931 21.3819H17.6694C20.1352 21.3819 21.6146 18.2025 22.601 15.0231C23.7695 11.2564 25.2985 6.21875 20.8749 6.21875H17.1167C16.0204 6.21875 15.0565 6.93987 14.7483 7.99192C14.0999 10.205 12.9666 14.0553 12.0761 16.9875C11.6229 18.4798 11.2453 19.7615 10.6659 20.5597C10.3411 21.0071 9.79971 21.3819 9.03931 21.3819Z"
                          fill="url(#paint4_radial_17090_63392)"
                        />
                        <path
                          d="M9.03931 21.3819H17.6694C20.1352 21.3819 21.6146 18.2025 22.601 15.0231C23.7695 11.2564 25.2985 6.21875 20.8749 6.21875H17.1167C16.0204 6.21875 15.0565 6.93987 14.7483 7.99192C14.0999 10.205 12.9666 14.0553 12.0761 16.9875C11.6229 18.4798 11.2453 19.7615 10.6659 20.5597C10.3411 21.0071 9.79971 21.3819 9.03931 21.3819Z"
                          fill="url(#paint5_linear_17090_63392)"
                        />
                        <path
                          d="M22.6398 15.834H9.12733C8.4669 15.834 7.93152 16.3694 7.93152 17.0298V21.6656C7.93152 22.326 8.4669 22.8614 9.12733 22.8614H22.6398C23.3002 22.8614 23.8356 22.326 23.8356 21.6656V17.0298C23.8356 16.3694 23.3002 15.834 22.6398 15.834Z"
                          fill="black"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.89917 17.1768V21.4611H9.69173V18.3745L10.5607 20.0861H11.0891L11.939 18.3731V21.4611H12.757V17.1768H11.8435L10.8508 19.1647L9.86364 17.1768H8.89917ZM14.8213 21.5153C14.3502 21.5153 13.983 21.4028 13.7199 21.1779C13.4589 20.9529 13.3284 20.6144 13.3284 20.1625H14.1114C14.1114 20.3768 14.173 20.5444 14.296 20.6654C14.4212 20.7864 14.5953 20.8468 14.8181 20.8468C15.0175 20.8468 15.1703 20.7959 15.2764 20.694C15.3846 20.5922 15.4387 20.4309 15.4387 20.2102C15.4387 19.998 15.373 19.8442 15.2414 19.7487C15.1119 19.6511 14.9433 19.6023 14.7353 19.6023H14.4775V18.9593H14.7576C14.9443 18.9593 15.0928 18.9084 15.2032 18.8065C15.3135 18.7046 15.3687 18.5699 15.3687 18.4023C15.3687 18.2325 15.3188 18.1073 15.2191 18.0267C15.1215 17.946 14.9868 17.9057 14.8149 17.9057C14.626 17.9057 14.4796 17.9588 14.3756 18.0649C14.2716 18.1688 14.2196 18.3121 14.2196 18.4946H13.4398C13.4398 18.2081 13.4992 17.9725 13.6181 17.7879C13.739 17.6033 13.9035 17.4654 14.1114 17.3741C14.3194 17.2808 14.5528 17.2341 14.8117 17.2341C15.0515 17.2341 15.2732 17.2733 15.4769 17.3519C15.6806 17.4304 15.844 17.5503 15.9671 17.7115C16.0923 17.8728 16.1549 18.0797 16.1549 18.3322C16.1549 18.589 16.0934 18.7927 15.9703 18.9434C15.8493 19.094 15.6859 19.2001 15.4801 19.2617C15.7072 19.3084 15.895 19.4144 16.0435 19.58C16.1942 19.7434 16.2695 19.9736 16.2695 20.2707C16.2695 20.6739 16.139 20.9826 15.878 21.197C15.6191 21.4091 15.2669 21.5153 14.8213 21.5153ZM17.3836 21.3052C17.6191 21.4473 17.8865 21.5184 18.1857 21.5184C18.487 21.5184 18.7416 21.459 18.9496 21.3402C19.1597 21.2213 19.3188 21.058 19.427 20.85C19.5353 20.6399 19.5894 20.399 19.5894 20.1274C19.5894 19.8452 19.5363 19.6086 19.4302 19.4176C19.3241 19.2245 19.1798 19.0792 18.9974 18.9816C18.817 18.8818 18.6133 18.8319 18.3862 18.8319C18.1952 18.8319 18.0276 18.8627 17.8833 18.9243C17.7411 18.9858 17.6149 19.0675 17.5045 19.1693C17.4906 19.1824 17.4769 19.1956 17.4633 19.209C17.4777 18.7979 17.5392 18.4865 17.6477 18.2749C17.7751 18.0288 17.9554 17.9057 18.1888 17.9057C18.3331 17.9057 18.4499 17.946 18.539 18.0267C18.6302 18.1052 18.6918 18.225 18.7236 18.3863H19.5066C19.4514 18.0171 19.3114 17.7338 19.0865 17.5365C18.8615 17.337 18.5623 17.2373 18.1888 17.2373C17.8854 17.2373 17.6159 17.3147 17.3804 17.4696C17.1448 17.6224 16.9602 17.859 16.8265 18.1794C16.6928 18.4977 16.626 18.9062 16.626 19.4049C16.626 19.9057 16.6939 20.312 16.8297 20.624C16.9655 20.9338 17.1501 21.1609 17.3836 21.3052ZM17.4658 19.8207C17.5222 19.7657 17.585 19.7152 17.6541 19.6691C17.826 19.5524 18.0202 19.494 18.2366 19.494C18.4 19.494 18.5273 19.5513 18.6186 19.6659C18.7098 19.7784 18.7554 19.9354 18.7554 20.137C18.7554 20.3428 18.7077 20.5136 18.6122 20.6495C18.5188 20.7853 18.3767 20.8532 18.1857 20.8532C18.0626 20.8532 17.9448 20.8139 17.8324 20.7354C17.722 20.6548 17.6318 20.5243 17.5618 20.3439C17.5102 20.2051 17.4782 20.0307 17.4658 19.8207ZM20.6812 21.3784C20.904 21.4718 21.1523 21.5184 21.426 21.5184C21.6976 21.5184 21.9417 21.4643 22.1581 21.3561C22.3767 21.2479 22.5496 21.0866 22.6769 20.8723C22.8043 20.6558 22.8679 20.3853 22.8679 20.0606C22.8679 19.7359 22.8075 19.4739 22.6865 19.2744C22.5677 19.0749 22.4117 18.9296 22.2186 18.8383C22.0255 18.745 21.8186 18.6983 21.5979 18.6983C21.4282 18.6983 21.2807 18.7259 21.1555 18.781C21.0324 18.8341 20.939 18.8967 20.8754 18.9688L20.9518 17.9885H22.5465V17.2914H20.2929L20.1114 19.5991H20.8404C20.8998 19.5248 20.9751 19.4633 21.0663 19.4144C21.1576 19.3657 21.269 19.3413 21.4006 19.3413C21.5873 19.3413 21.739 19.3985 21.8558 19.5131C21.9746 19.6256 22.034 19.8081 22.034 20.0606C22.034 20.3344 21.9778 20.5349 21.8653 20.6622C21.755 20.7895 21.6085 20.8532 21.426 20.8532C21.2329 20.8532 21.0748 20.7991 20.9518 20.6909C20.8287 20.5805 20.7671 20.4298 20.7671 20.2389H19.9555C19.9555 20.5253 20.0202 20.7641 20.1496 20.955C20.2812 21.1439 20.4584 21.285 20.6812 21.3784Z"
                          fill="white"
                        />
                        <defs>
                          <radialGradient
                            id="paint0_radial_17090_63392"
                            cx="0"
                            cy="0"
                            r="1"
                            gradientUnits="userSpaceOnUse"
                            gradientTransform="translate(19.5407 9.22088) rotate(-130.285) scale(9.08814 8.59973)"
                          >
                            <stop offset="0.0955758" stopColor="#00AEFF" />
                            <stop offset="0.773185" stopColor="#2253CE" />
                            <stop offset="1" stopColor="#0736C4" />
                          </radialGradient>
                          <radialGradient
                            id="paint1_radial_17090_63392"
                            cx="0"
                            cy="0"
                            r="1"
                            gradientUnits="userSpaceOnUse"
                            gradientTransform="translate(5.05012 15.758) rotate(50.2556) scale(8.22385 8.07248)"
                          >
                            <stop stopColor="#FFB657" />
                            <stop offset="0.633728" stopColor="#FF5F3D" />
                            <stop offset="0.923392" stopColor="#C02B3C" />
                          </radialGradient>
                          <linearGradient
                            id="paint2_linear_17090_63392"
                            x1="5.81224"
                            y1="2.50749"
                            x2="6.98502"
                            y2="16.4124"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop offset="0.156162" stopColor="#0D91E1" />
                            <stop offset="0.487484" stopColor="#52B471" />
                            <stop offset="0.652394" stopColor="#98BD42" />
                            <stop offset="0.937361" stopColor="#FFC800" />
                          </linearGradient>
                          <linearGradient
                            id="paint3_linear_17090_63392"
                            x1="6.88836"
                            y1="0.669922"
                            x2="7.5286"
                            y2="15.8366"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#3DCBFF" />
                            <stop
                              offset="0.246674"
                              stopColor="#0588F7"
                              stopOpacity="0"
                            />
                          </linearGradient>
                          <radialGradient
                            id="paint4_radial_17090_63392"
                            cx="0"
                            cy="0"
                            r="1"
                            gradientUnits="userSpaceOnUse"
                            gradientTransform="translate(21.3182 4.7342) rotate(109.722) scale(20.2035 24.6753)"
                          >
                            <stop offset="0.0661714" stopColor="#8C48FF" />
                            <stop offset="0.5" stopColor="#F2598A" />
                            <stop offset="0.895833" stopColor="#FFB152" />
                          </radialGradient>
                          <linearGradient
                            id="paint5_linear_17090_63392"
                            x1="22"
                            y1="5.29301"
                            x2="21.9916"
                            y2="9.42291"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop offset="0.0581535" stopColor="#F8ADFA" />
                            <stop
                              offset="0.708063"
                              stopColor="#A86EDD"
                              stopOpacity="0"
                            />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">
                        Microsoft 365
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Documents, spreadsheets, and presentations
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #374151",
                      color: "#374151",
                    }}
                    className="hover:bg-gray-50 rounded-md px-4 py-2 font-medium transition-colors"
                    data-testid="connected-apps-connect-microsoft-365"
                  >
                    Connect
                  </button>
                </div>

                {/* Google Workspace */}
                <div className="border-gray-100 flex items-center justify-between border-b py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex size-10 items-center justify-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_17090_63356)">
                          <path
                            d="M8.36061 0.789921C5.96264 1.6218 3.89463 3.20073 2.46035 5.2948C1.02606 7.38887 0.301099 9.88769 0.391944 12.4242C0.48279 14.9608 1.38466 17.4013 2.96507 19.3874C4.54549 21.3735 6.72115 22.8005 9.17249 23.4587C11.1598 23.9715 13.242 23.994 15.24 23.5243C17.05 23.1177 18.7233 22.2481 20.0962 21.0005C21.5251 19.6624 22.5623 17.9602 23.0962 16.0768C23.6766 14.0287 23.7799 11.8748 23.3981 9.78055H12.2381V14.4099H18.7012C18.5721 15.1483 18.2953 15.853 17.8874 16.4819C17.4795 17.1107 16.949 17.6509 16.3275 18.0699C15.5383 18.592 14.6486 18.9432 13.7156 19.1012C12.7799 19.2752 11.8201 19.2752 10.8844 19.1012C9.93597 18.9051 9.0388 18.5137 8.24999 17.9518C6.98277 17.0548 6.03126 15.7804 5.53124 14.3105C5.02277 12.8132 5.02277 11.1898 5.53124 9.69242C5.88716 8.64283 6.47555 7.68717 7.25249 6.8968C8.1416 5.9757 9.26724 5.31729 10.5059 4.99381C11.7446 4.67034 13.0484 4.69429 14.2744 5.06305C15.2321 5.35703 16.1078 5.87068 16.8319 6.56305C17.5606 5.83805 18.2881 5.11117 19.0144 4.38242C19.3894 3.99055 19.7981 3.61742 20.1675 3.21617C19.0623 2.18769 17.765 1.3874 16.35 0.861171C13.7732 -0.0744733 10.9537 -0.0996178 8.36061 0.789921Z"
                            fill="white"
                          />
                          <path
                            d="M8.36058 0.788879C10.9535 -0.101264 13.7729 -0.0767817 16.35 0.858254C17.7652 1.38806 19.0619 2.1922 20.1656 3.2245C19.7906 3.62575 19.395 4.00075 19.0125 4.39075C18.285 5.117 17.5581 5.84075 16.8318 6.562C16.1078 5.86964 15.232 5.35599 14.2743 5.062C13.0488 4.69196 11.745 4.66662 10.506 4.98877C9.26699 5.31092 8.14067 5.96812 7.25058 6.88825C6.47364 7.67863 5.88525 8.63428 5.52933 9.68388L1.64246 6.6745C3.03372 3.91556 5.44261 1.80517 8.36058 0.788879Z"
                            fill="#E33629"
                          />
                          <path
                            d="M0.611279 9.65703C0.820194 8.62164 1.16704 7.61896 1.64253 6.67578L5.5294 9.69266C5.02093 11.19 5.02093 12.8134 5.5294 14.3108C4.2344 15.3108 2.93878 16.3158 1.64253 17.3258C0.452186 14.9564 0.0891526 12.2567 0.611279 9.65703Z"
                            fill="#F8BD00"
                          />
                          <path
                            d="M12.2381 9.77734H23.3981C23.7799 11.8716 23.6766 14.0255 23.0963 16.0736C22.5623 17.957 21.5252 19.6592 20.0963 20.9973C18.8419 20.0186 17.5819 19.0473 16.3275 18.0686C16.9494 17.6491 17.4802 17.1084 17.8881 16.4788C18.296 15.8493 18.5726 15.1439 18.7013 14.4048H12.2381C12.2363 12.8636 12.2381 11.3205 12.2381 9.77734Z"
                            fill="#587DBD"
                          />
                          <path
                            d="M1.64062 17.3255C2.93687 16.3255 4.2325 15.3205 5.5275 14.3105C6.02851 15.7809 6.98138 17.0553 8.25 17.9518C9.04127 18.511 9.94037 18.8993 10.89 19.0918C11.8257 19.2658 12.7855 19.2658 13.7213 19.0918C14.6542 18.9339 15.5439 18.5826 16.3331 18.0605C17.5875 19.0393 18.8475 20.0105 20.1019 20.9893C18.7292 22.2375 17.0558 23.1078 15.2456 23.5149C13.2476 23.9846 11.1655 23.9621 9.17813 23.4493C7.60632 23.0296 6.13814 22.2898 4.86563 21.2762C3.51874 20.2068 2.41867 18.8592 1.64062 17.3255Z"
                            fill="#319F43"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_17090_63356">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">
                        Google Workspace
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Documents, spreadsheets, and presentations
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #374151",
                      color: "#374151",
                    }}
                    className="hover:bg-gray-50 rounded-md px-4 py-2 font-medium transition-colors"
                    data-testid="connected-apps-connect-google-workspace"
                  >
                    Connect
                  </button>
                </div>

                {/* Adobe Creative Cloud */}
                <div className="border-gray-100 flex items-center justify-between border-b py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex size-10 items-center justify-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 3.00006C13.7284 2.99557 12.4702 3.26061 11.3085 3.77771C10.1467 4.29481 9.10775 5.05223 8.26 6.00006C8.00685 5.98629 7.75315 5.98629 7.5 6.00006C5.51088 6.00006 3.60322 6.79023 2.1967 8.19675C0.790176 9.60328 0 11.5109 0 13.5001C0 15.4892 0.790176 17.3968 2.1967 18.8034C3.60322 20.2099 5.51088 21.0001 7.5 21.0001H15C17.3869 21.0001 19.6761 20.0518 21.364 18.364C23.0518 16.6762 24 14.387 24 12.0001C24 9.61311 23.0518 7.32392 21.364 5.63609C19.6761 3.94827 17.3869 3.00006 15 3.00006ZM8.5 18.7501H7.5C6.64044 18.7442 5.79548 18.5273 5.03931 18.1186C4.28315 17.7098 3.63892 17.1216 3.1632 16.4057C2.68748 15.6898 2.39483 14.868 2.31095 14.0125C2.22707 13.157 2.35452 12.294 2.68211 11.4993C3.00971 10.7046 3.52742 10.0025 4.18979 9.45468C4.85215 8.90682 5.6389 8.52999 6.48095 8.35726C7.32299 8.18454 8.19456 8.22121 9.01912 8.46406C9.84368 8.7069 10.596 9.1485 11.21 9.75006L13.57 12.1001C13.7105 12.2407 13.7893 12.4313 13.7893 12.6301C13.7893 12.8288 13.7105 13.0194 13.57 13.1601C13.5008 13.2302 13.4182 13.2857 13.3272 13.3235C13.2362 13.3613 13.1385 13.3805 13.04 13.3801C12.9414 13.3811 12.8436 13.3621 12.7525 13.3243C12.6614 13.2865 12.5789 13.2306 12.51 13.1601L10.15 10.8101C9.71404 10.3736 9.17711 10.0513 8.58683 9.872C7.99654 9.69266 7.37112 9.66172 6.76602 9.78193C6.16091 9.90214 5.59481 10.1698 5.1179 10.5611C4.64098 10.9525 4.26798 11.4554 4.03197 12.0254C3.79595 12.5954 3.70421 13.2149 3.76488 13.8288C3.82554 14.4427 4.03674 15.0322 4.37974 15.545C4.72275 16.0578 5.18697 16.478 5.73125 16.7685C6.27554 17.0589 6.88307 17.2106 7.5 17.2101H8.5C8.69891 17.2101 8.88968 17.2891 9.03033 17.4297C9.17098 17.5704 9.25 17.7611 9.25 17.9601C9.25 18.159 9.17098 18.3497 9.03033 18.4904C8.88968 18.631 8.69891 18.7101 8.5 18.7101V18.7501ZM15 18.7501C14.1119 18.7505 13.2326 18.5739 12.4136 18.2305C11.5945 17.8871 10.8522 17.3838 10.23 16.7501L7 13.5201C6.92924 13.4511 6.87301 13.3687 6.83461 13.2777C6.79621 13.1866 6.77643 13.0888 6.77643 12.9901C6.77643 12.8913 6.79621 12.7935 6.83461 12.7024C6.87301 12.6114 6.92924 12.529 7 12.4601C7.14063 12.3196 7.33125 12.2407 7.53 12.2407C7.72875 12.2407 7.91937 12.3196 8.06 12.4601L11.31 15.7101C12.0216 16.4213 12.9227 16.913 13.9058 17.1268C14.889 17.3405 15.9128 17.2673 16.8555 16.9158C17.7982 16.5642 18.6201 15.9492 19.2232 15.1439C19.8264 14.3387 20.1854 13.377 20.2577 12.3735C20.3299 11.37 20.1122 10.3668 19.6306 9.48349C19.149 8.60014 18.4237 7.87379 17.541 7.39088C16.6584 6.90798 15.6556 6.68887 14.6519 6.75964C13.6483 6.83041 12.6862 7.18807 11.88 7.79006C11.7246 7.89428 11.5359 7.93668 11.3509 7.90892C11.1659 7.88117 10.9979 7.78528 10.88 7.64006C10.7791 7.48284 10.7397 7.29398 10.7692 7.10952C10.7987 6.92507 10.8951 6.75794 11.04 6.64006C12.1922 5.77046 13.5965 5.30005 15.04 5.30006C16.8302 5.30006 18.5471 6.01121 19.813 7.27708C21.0788 8.54296 21.79 10.2598 21.79 12.0501C21.79 13.8403 21.0788 15.5572 19.813 16.823C18.5471 18.0889 16.8302 18.8001 15.04 18.8001L15 18.7501Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">
                        Adobe Creative Cloud
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Photoshop, Illustrator, and InDesign files
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #374151",
                      color: "#374151",
                    }}
                    className="hover:bg-gray-50 rounded-md px-4 py-2 font-medium transition-colors"
                    data-testid="connected-apps-connect-adobe-creative-cloud"
                  >
                    Connect
                  </button>
                </div>

                {/* Monday.com */}
                <div className="flex items-center justify-between py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex size-10 items-center justify-center">
                      <svg
                        width="24"
                        height="15"
                        viewBox="0 0 24 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_17025_51760)">
                          <path
                            d="M2.98554 14.7585C2.45499 14.7597 1.93365 14.6164 1.47477 14.3433C1.01589 14.0702 0.635917 13.677 0.373665 13.204C0.115413 12.7343 -0.0133859 12.2014 0.00106453 11.6624C0.0155149 11.1234 0.172671 10.5985 0.455696 10.1441L5.83441 1.48103C6.1097 1.01543 6.50055 0.633126 6.96704 0.373171C7.43353 0.113216 7.95895 -0.0150733 8.4896 0.00140997C9.01986 0.0144279 9.53722 0.171715 9.98873 0.457174C10.4402 0.742633 10.8097 1.14601 11.0593 1.62603C11.5608 2.59843 11.498 3.76833 10.8976 4.68122L5.52223 13.3442C5.24965 13.7799 4.87425 14.1379 4.43081 14.3852C3.98737 14.6324 3.49026 14.7608 2.98554 14.7585Z"
                            fill="#F62B54"
                          />
                          <path
                            d="M12.2115 14.7584C11.1266 14.7584 10.1284 14.1635 9.60413 13.2075C9.34645 12.7391 9.21794 12.2076 9.23239 11.67C9.24684 11.1324 9.40371 10.609 9.68616 10.1558L15.0547 1.51258C15.3259 1.04044 15.7154 0.651334 16.1827 0.385631C16.65 0.119928 17.1781 -0.0126945 17.7122 0.00152218C18.8062 0.0260414 19.7976 0.653638 20.2989 1.63546C20.5437 2.11623 20.655 2.65667 20.6209 3.19788C20.5867 3.73909 20.4084 4.2603 20.1053 4.7047L14.7379 13.3479C14.4661 13.7817 14.0923 14.1383 13.6507 14.3848C13.2092 14.6314 12.7142 14.7598 12.2115 14.7584Z"
                            fill="#FFCC00"
                          />
                          <path
                            d="M21.2312 14.8312C22.7606 14.8312 24.0004 13.5863 24.0004 12.0506C24.0004 10.5149 22.7606 9.27002 21.2312 9.27002C19.7018 9.27002 18.462 10.5149 18.462 12.0506C18.462 13.5863 19.7018 14.8312 21.2312 14.8312Z"
                            fill="#00CA72"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_17025_51760">
                            <rect width="24" height="15" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">Monday.com</h3>
                      <p className="text-gray-600 text-sm">
                        Campaign boards and marketing assets
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #374151",
                      color: "#374151",
                    }}
                    className="hover:bg-gray-50 rounded-md px-4 py-2 font-medium transition-colors"
                    data-testid="connected-apps-connect-monday"
                  >
                    Connect
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
